import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { clearAuthStorage, getStoredAuth } from '../utils/authStorage';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_API_URL || '/api';
const LOGIN_TIMEOUT_MS = 15000;

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginAbortRef = useRef(null);

  useEffect(() => {
    const { token: storedToken, user: storedUser } = getStoredAuth();
    setToken(storedToken);
    setUser(storedUser);
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    if (loginAbortRef.current) {
      loginAbortRef.current.abort();
    }

    const controller = new AbortController();
    loginAbortRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);

    clearAuthStorage();
    setToken(null);
    setUser(null);

    try {
      const response = await axios.post(
        `${API_URL}/auth/session`,
        { username: username.trim(), secret: password },
        {
          signal: controller.signal,
          timeout: LOGIN_TIMEOUT_MS,
          headers: { Authorization: undefined }
        }
      );

      const { token: newToken, username: loggedInUser, role } = response.data;

      if (!newToken) {
        return { success: false, error: 'Respuesta inválida del servidor' };
      }

      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify({ username: loggedInUser, role }));

      setToken(newToken);
      setUser({ username: loggedInUser, role });

      return { success: true };
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        return {
          success: false,
          error: 'La petición tardó demasiado. Puede estar bloqueada por Kaspersky o el antivirus. Inténtalo de nuevo.'
        };
      }

      if (process.env.NODE_ENV !== 'production') console.error('Login error:', error);

      if (!error.response) {
        const blockedByClient = error.message === 'Network Error' || error.code === 'ERR_NETWORK';
        return {
          success: false,
          error: blockedByClient
            ? 'La petición fue bloqueada (Kaspersky, antivirus o extensión). Añade localhost a las exclusiones.'
            : 'No se puede conectar con el servidor. Comprueba que el backend esté en marcha.'
        };
      }

      if (error.response.status === 403) {
        return {
          success: false,
          error: 'Acceso denegado (403). Reinicia el backend desde IntelliJ.'
        };
      }

      if (error.response.status === 401) {
        return { success: false, error: 'Credenciales inválidas' };
      }

      const errorMessage = error.response?.data?.error || error.response?.data || 'Error al iniciar sesión';
      return {
        success: false,
        error: typeof errorMessage === 'string' && errorMessage.trim() ? errorMessage : 'Error al iniciar sesión'
      };
    } finally {
      clearTimeout(timeoutId);
      if (loginAbortRef.current === controller) {
        loginAbortRef.current = null;
      }
    }
  };

  const logout = () => {
    if (loginAbortRef.current) {
      loginAbortRef.current.abort();
      loginAbortRef.current = null;
    }
    clearAuthStorage();
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthProvider, useAuth };
